import {
  FlexBoxView,
  FlexDirection,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { View } from '@play-co/timestep-core/ui';
import Checkbox from 'src/game/components/shared/Checkbox';
import { user } from 'src/StateObserver';
import { SettingsKey } from '../popup/PopupSettings';
import StateObserver from 'src/StateObserver';
import { NotificationSettings } from 'src/replicant/chatbot/chatbot.ruleset';
import { CheckboxGroup } from 'src/game/components/shared/CheckboxGroup';
import sounds from 'src/lib/sounds';
import { Locale } from 'src/replicant/ruleset/locale';
import i18n, { loadableLocales } from 'src/lib/i18n/i18n';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';

const languages = {
  en: 'ENGLISH',
  ja: '日本語',
};

const languageData = Object.keys(loadableLocales).map((locale, index) => {
  return {
    index,
    key: locale,
    localeText: () => languages[locale as Locale],
  };
});

type SettingsCheckboxGroupKey = Extract<
  SettingsKey,
  'locale' | 'notifications' | 'decorationEditor'
>;
type SettingsCheckboxGroupOpts = {
  superview: View;
  type: SettingsCheckboxGroupKey;
};

export class SettingsCheckboxGroup extends FlexBoxView {
  private opts: SettingsCheckboxGroupOpts;

  constructor(opts: SettingsCheckboxGroupOpts) {
    super({
      superview: opts.superview,
      flex: {
        height: '100%',
        padding: opts.type === 'locale' ? 0 : 5,
      },
    });

    this.opts = opts;
    this.createCheckboxGroup();
  }

  private createCheckboxGroup() {
    const { options, selected } = this.getSettingOptions();
    const checkboxes = options.map((option) =>
      this.createCheckboxItem(option, option === selected),
    );

    new CheckboxGroup({
      options: checkboxes,
      onSelect: (value: string) => {
        sounds.sfx.play('ok');
        this.handleSettingChange(value);
      },
    });
  }

  private createCheckboxItem(value: string, isChecked: boolean) {
    const opts = this.getLabelOptions(value);

    const wrapper = new FlexBoxView({
      superview: this,
      flex: {
        flexDirection: opts.direction,
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    const checkbox = new Checkbox({
      superview: wrapper,
      size: 54,
      isChecked,
      tracking: {
        feature: 'settings',
        $subFeature: opts.subFeature,
      },
    });
    getFlexNode(checkbox).flexShrink = 0;

    new ButtonScaleViewWithText({
      superview: wrapper,
      labelHorizontalAlign: opts.direction === 'row' ? 'left' : 'center',
      font: 'Body',
      textColor: '#BF8147',
      ...opts,
      onClick: async () => checkbox.setChecked(true),
      tracking: {
        feature: 'settings',
        $subFeature: opts.subFeature,
      },
    });

    return { id: value, checkbox };
  }

  private getSettingOptions(): { options: string[]; selected: string } {
    switch (this.opts.type) {
      case 'locale':
        return {
          options: languageData.map((lang) => lang.key).reverse(),
          selected: user().settings.locale,
        };
      case 'notifications':
        return {
          options: Object.values(NotificationSettings),
          selected: user().settings.receiveMessages,
        };
      case 'decorationEditor':
        return {
          options: ['Old', 'New'],
          selected: user().settings.decorationEditor ? 'Old' : 'New',
        };
    }
  }

  private getLabelOptions(value: string): {
    direction: FlexDirection;
    width: number;
    height: number;
    fontSize: number;
    localeText?: () => string;
    subFeature: string;
  } {
    const direction: FlexDirection =
      this.opts.type === 'locale' ? 'row' : 'column';
    switch (this.opts.type) {
      case 'locale': {
        const lang = languageData.find((lang) => lang.key === value);
        return {
          direction,
          width: 125,
          height: 45,
          fontSize: value === 'ja' ? 28 : 25,
          localeText: lang?.localeText,
          subFeature: `popup-settings-language-${value}`,
        };
      }
      case 'notifications':
        return {
          direction,
          width: 77,
          height: 25,
          fontSize: 21,
          localeText: () => i18n(`settings.${value}`),
          subFeature: `popup-settings-receiveMessages-${value}`,
        };
      case 'decorationEditor':
        return {
          direction,
          width: 77,
          height: 25,
          fontSize: 21,
          localeText: () => value.toLocaleUpperCase(),
          subFeature: `popup-settings-decorationEditor-${value}`,
        };
    }
  }

  private async handleSettingChange(value: string): Promise<void> {
    switch (this.opts.type) {
      case 'locale':
        await StateObserver.invoke.updateSettings({ locale: value });
        break;
      case 'notifications':
        await StateObserver.invoke.updateSettings({
          receiveMessages: value as NotificationSettings,
        });
        break;
      case 'decorationEditor':
        await StateObserver.invoke.updateSettings({
          decorationEditor: value === 'Old',
        });
        break;
    }
  }
}
